import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import Button from '../components/ui/Button';
import NetworkVisual from '../components/visuals/NetworkVisual';
import WallOfDominance from '../components/sections/WallOfDominance';

const RedditService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const includes = [
    {
      title: "Relevant Subreddits",
      desc: "Strategic placements within communities that are obsessed with your niche.",
      icon: FaIcons.FaReddit
    },
    {
      title: "Niche Targeting",
      desc: "Identifying the exact sub-communities where your target audience hangs out.",
      icon: FiIcons.FiTarget
    },
    {
      title: "Natural Integration",
      desc: "Brand mentions that flow naturally within ongoing discussions and threads.",
      icon: FiIcons.FiMessageSquare
    }
  ];

  const redditProofImages = [
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/c57d80fc87bb8c7140b0bef7698b4e17.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/987260b89eb4f02b0d866c662cacae9e.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/2ac3cc730c90cdd9c57013361e9e05e8.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/ae69de73cb21c2ff4c1acf28bca02fec.png",
    "https://media-manager-c.questera.ai/greta-media/22508bb601940b4e20f7d160303c50efd71c7583e6bf992668d180d798243f38c2166642d2b71e0a7892275161f0f512/images/aW1hZ2UvcG5n/7cc1400b683a876507c97c64834e795f.png"
  ];

  const benefits = [
    "Drive targeted traffic from engaged communities",
    "Increase brand awareness organically",
    "Build credibility through authentic discussions",
    "Capture search-driven visibility in Google",
    "Support broader SEO and content strategies"
  ];

  return (
    <div className="bg-[#0B0F19] min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden border-b border-white/5">
        <NetworkVisual />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center flex flex-col items-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-7xl font-extrabold text-white mb-6 leading-tight tracking-tight"
            >
              Reddit <span className="text-orange-500">Services</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl md:text-2xl text-slate-400 mb-10 leading-relaxed max-w-2xl mx-auto"
            >
              Increase your brand visibility and engage with targeted communities through strategic Reddit placements.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-6 w-full sm:w-auto"
            >
              <Button href="#/contact" variant="primary" className="!from-orange-600 !to-red-600 !px-12 !py-5 text-xl font-bold shadow-[0_0_40px_rgba(255,69,0,0.4)] w-full sm:w-auto">
                Start Engaging Now
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wall of Dominance / Proof Section */}
      <WallOfDominance 
        images={redditProofImages} 
        title="Proven Reddit Placements" 
        subtitle="Click any screenshot to zoom in. Use arrows to switch between results." 
        glowColor="orange"
      />

      {/* Section 1: Real Conversations (Stacked & Centralized) */}
      <section className="py-24 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Reach Your Audience Through Real Conversations</h2>
          <div className="text-slate-400 space-y-8 text-xl leading-relaxed">
            <p>
              Reddit is one of the most active platforms for discussions, recommendations, and opinions. Users rely on community-driven content when researching products, services, and solutions.
            </p>
            <p>
              PRBoost helps businesses and agencies position their brand within relevant Reddit communities, ensuring your message appears naturally within ongoing discussions.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Service Includes (Stacked & Centralized) */}
      <section className="py-24 bg-slate-900/30 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 tracking-tight">What This Service Includes</h2>
            <p className="text-slate-400 text-xl font-medium">Comprehensive community management and strategic placement.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {includes.map((item, idx) => (
              <motion.div 
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="p-10 rounded-[2.5rem] glass-card border-white/5 hover:border-orange-500/20 transition-all group flex flex-col items-center text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-orange-500/10 flex items-center justify-center text-orange-400 mb-8 group-hover:scale-110 transition-transform">
                  <SafeIcon icon={item.icon} className="text-3xl" />
                </div>
                <h3 className="text-white font-bold text-2xl mb-4">{item.title}</h3>
                <p className="text-slate-400 text-lg leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Why Reddit Works (Centralized) */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass-card rounded-[4rem] p-12 md:p-24 border-orange-500/20 bg-gradient-to-br from-slate-900/90 to-orange-900/10 text-center">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-10 tracking-tight">Why Reddit Works</h2>
            <p className="text-slate-400 mb-16 leading-relaxed text-2xl max-w-3xl mx-auto">
              Reddit content often ranks on Google, allowing discussions and threads to appear in search results. This creates an opportunity to reach users both on Reddit and through search engines.
            </p>
            <div className="flex justify-center">
              <ul className="space-y-8 text-left inline-flex flex-col">
                {benefits.map((benefit, i) => (
                  <li key={i} className="flex items-center gap-6 text-slate-300 text-2xl">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="text-orange-400 text-3xl flex-shrink-0" />
                    <span className="font-semibold">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works (Centralized) */}
      <section className="py-24 bg-[#070b14]">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-24 text-center tracking-tight">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-16 relative max-w-6xl mx-auto">
            {[
              { num: "1", title: "Identify Discussions", desc: "We find relevant subreddits and active threads where your brand adds value." },
              { num: "2", title: "Create Content", desc: "Our experts draft responses aligned with community expectations and rules." },
              { num: "3", title: "Position & Engage", desc: "We publish and position your brand naturally within the conversation." }
            ].map((step, i) => (
              <div key={i} className="text-center group">
                <div className="relative inline-block mb-10">
                  <div className="w-24 h-24 rounded-[2.5rem] bg-orange-600 flex items-center justify-center text-white text-4xl font-bold shadow-[0_0_50px_rgba(255,69,0,0.3)] group-hover:scale-110 transition-transform">
                    {step.num}
                  </div>
                </div>
                <h4 className="text-3xl font-bold text-white mb-5">{step.title}</h4>
                <p className="text-slate-400 text-xl leading-relaxed max-w-[300px] mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-orange-600/5 blur-[120px] rounded-full"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 tracking-tight">Start Engaging with <br/><span className="text-orange-500">Your Audience</span></h2>
          <p className="text-2xl md:text-3xl text-slate-400 mb-16 max-w-3xl mx-auto leading-relaxed">
            Reddit offers a unique opportunity to connect with users through real conversations.
          </p>
          <div className="flex justify-center">
            <Button href="#/contact" variant="primary" className="!from-orange-600 !to-red-600 !px-20 !py-7 text-3xl font-bold shadow-[0_0_60px_rgba(255,69,0,0.4)]">
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RedditService;
